import React, { useState } from 'react';
import { Link } from '@inertiajs/react';
import MainLayout from '../../layouts/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { FiArrowLeft, FiDownload, FiCalendar, FiUsers, FiDollarSign, FiHome } from 'react-icons/fi';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';

export default function AnalyticsShow({ facility, analytics, snapshots, historical_trends, seasonal_trends, user }) {
  const [activeTab, setActiveTab] = useState('occupancy');
  
  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Prepare data for occupancy trend chart
  const occupancyTrendData = snapshots.map(snapshot => ({
    date: formatDate(snapshot.on_date),
    occupancy: snapshot.occupancy_rate,
    available: snapshot.units_available,
    total: snapshot.units_total
  })).reverse(); // Show oldest to newest

  // Prepare data for lead source pie chart
  const leadSourceData = [
    { name: 'Website', value: analytics.leads.website_inquiry || 0 },
    { name: 'Tour Request', value: analytics.leads.tour_request || 0 },
    { name: 'Referral', value: analytics.leads.referral || 0 },
    { name: 'Other', value: analytics.leads.other || 0 }
  ];

  // Colors for pie chart
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

  return (
    <MainLayout user={user}>
      <div className="py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-6">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
              <div className="flex items-center">
                <Button variant="ghost" size="sm" asChild className="mr-4">
                  <Link href="/facility_owner/analytics">
                    <FiArrowLeft className="mr-2 h-4 w-4" />
                    Back to Analytics
                  </Link>
                </Button>
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold text-gray-900">{facility.name} Analytics</h1>
                  <p className="text-gray-500">{facility.address}</p>
                </div>
              </div>
              <Button variant="outline" size="sm" className="mt-4 md:mt-0">
                <FiDownload className="mr-2 h-4 w-4" />
                Export Report
              </Button>
            </div>
          </div>
          
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            {/* Occupancy Card */}
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="p-2 rounded-full bg-blue-100">
                    <FiHome className="h-5 w-5 text-blue-600" />
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-500">Occupancy Rate</p>
                    <p className="text-2xl font-bold">{analytics.availability.occupancy_rate}%</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Leads Card */}
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="p-2 rounded-full bg-green-100">
                    <FiUsers className="h-5 w-5 text-green-600" />
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-500">Total Leads</p>
                    <p className="text-2xl font-bold">{analytics.leads.total_count}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Tours Card */}
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="p-2 rounded-full bg-purple-100">
                    <FiCalendar className="h-5 w-5 text-purple-600" />
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-500">Tours Scheduled</p>
                    <p className="text-2xl font-bold">{analytics.tours.total_count}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Revenue Card */}
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="p-2 rounded-full bg-amber-100">
                    <FiDollarSign className="h-5 w-5 text-amber-600" />
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-500">Est. Monthly Revenue</p>
                    <p className="text-2xl font-bold">${analytics.financial.estimated_monthly_revenue}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Detailed Analytics Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-5">
              <TabsTrigger value="occupancy">Occupancy</TabsTrigger>
              <TabsTrigger value="leads">Leads</TabsTrigger>
              <TabsTrigger value="tours">Tours</TabsTrigger>
              <TabsTrigger value="financial">Financial</TabsTrigger>
              <TabsTrigger value="trends">Trends</TabsTrigger>
            </TabsList>
            
            {/* Occupancy Tab */}
            <TabsContent value="occupancy">
              <Card>
                <CardHeader>
                  <CardTitle>Occupancy Trends</CardTitle>
                  <CardDescription>30-day historical occupancy rate</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart
                        data={occupancyTrendData}
                        margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Line
                          type="monotone"
                          dataKey="occupancy"
                          name="Occupancy Rate (%)"
                          stroke="#8884d8"
                          activeDot={{ r: 8 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                    <div className="p-4 border rounded-md">
                      <h3 className="font-medium mb-2">Current Availability</h3>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <p className="text-sm text-gray-500">Total Units</p>
                          <p className="text-xl font-bold">{analytics.availability.total_units}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Available Units</p>
                          <p className="text-xl font-bold">{analytics.availability.available_units}</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="p-4 border rounded-md">
                      <h3 className="font-medium mb-2">Occupancy Metrics</h3>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <p className="text-sm text-gray-500">Current Rate</p>
                          <p className="text-xl font-bold">{analytics.availability.occupancy_rate}%</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">30-Day Average</p>
                          <p className="text-xl font-bold">{analytics.availability.occupancy_rate}%</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            
            {/* Leads Tab */}
            <TabsContent value="leads">
              <Card>
                <CardHeader>
                  <CardTitle>Lead Analytics</CardTitle>
                  <CardDescription>Lead sources and conversion metrics</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h3 className="font-medium mb-4">Lead Sources</h3>
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={leadSourceData}
                              cx="50%"
                              cy="50%"
                              labelLine={false}
                              outerRadius={80}
                              fill="#8884d8"
                              dataKey="value"
                              label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                            >
                              {leadSourceData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                    
                    <div>
                      <h3 className="font-medium mb-4">Conversion Metrics</h3>
                      <div className="space-y-4">
                        <div className="p-4 border rounded-md">
                          <div className="flex justify-between items-center">
                            <p className="text-sm text-gray-500">Lead to Tour Rate</p>
                            <p className="text-lg font-bold">{analytics.conversion.lead_to_tour}%</p>
                          </div>
                        </div>
                        <div className="p-4 border rounded-md">
                          <div className="flex justify-between items-center">
                            <p className="text-sm text-gray-500">Tour to Move-in Rate</p>
                            <p className="text-lg font-bold">{analytics.conversion.tour_to_move_in}%</p>
                          </div>
                        </div>
                        <div className="p-4 border rounded-md">
                          <div className="flex justify-between items-center">
                            <p className="text-sm text-gray-500">Overall Conversion Rate</p>
                            <p className="text-lg font-bold">{analytics.conversion.lead_to_move_in}%</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            
            {/* Tours Tab */}
            <TabsContent value="tours">
              <Card>
                <CardHeader>
                  <CardTitle>Tour Analytics</CardTitle>
                  <CardDescription>Tour statistics and outcomes</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h3 className="font-medium mb-4">Tour Status Distribution</h3>
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart
                            data={[
                              { name: 'Pending', value: analytics.tours.pending || 0 },
                              { name: 'Completed', value: analytics.tours.completed || 0 },
                              { name: 'Cancelled', value: analytics.tours.cancelled || 0 },
                              { name: 'No-show', value: analytics.tours.no_show || 0 }
                            ]}
                            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" />
                            <YAxis />
                            <Tooltip />
                            <Bar dataKey="value" name="Count" fill="#8884d8" />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                    
                    <div>
                      <h3 className="font-medium mb-4">Tour Metrics</h3>
                      <div className="space-y-4">
                        <div className="p-4 border rounded-md">
                          <div className="flex justify-between items-center">
                            <p className="text-sm text-gray-500">Total Tours Scheduled</p>
                            <p className="text-lg font-bold">{analytics.tours.total}</p>
                          </div>
                        </div>
                        <div className="p-4 border rounded-md">
                          <div className="flex justify-between items-center">
                            <p className="text-sm text-gray-500">Tours Completed</p>
                            <p className="text-lg font-bold">{analytics.tours.completed}</p>
                          </div>
                        </div>
                        <div className="p-4 border rounded-md">
                          <div className="flex justify-between items-center">
                            <p className="text-sm text-gray-500">Completion Rate</p>
                            <p className="text-lg font-bold">{analytics.tours.completion_rate}%</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            
            {/* Financial Tab */}
            <TabsContent value="financial">
              <Card>
                <CardHeader>
                  <CardTitle>Financial Analytics</CardTitle>
                  <CardDescription>Revenue and occupancy financial impact</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="p-4 border rounded-md">
                      <h3 className="font-medium mb-4">Revenue Metrics</h3>
                      <div className="space-y-4">
                        <div className="flex justify-between items-center">
                          <p className="text-sm text-gray-500">Est. Monthly Revenue</p>
                          <p className="text-lg font-bold">${analytics.financial.estimated_monthly_revenue}</p>
                        </div>
                        <div className="flex justify-between items-center">
                          <p className="text-sm text-gray-500">Avg. Unit Revenue</p>
                          <p className="text-lg font-bold">${analytics.financial.average_unit_revenue}</p>
                        </div>
                        <div className="flex justify-between items-center">
                          <p className="text-sm text-gray-500">Revenue per Sq Ft</p>
                          <p className="text-lg font-bold">${analytics.financial.revenue_per_sqft}</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="p-4 border rounded-md">
                      <h3 className="font-medium mb-4">Occupancy Impact</h3>
                      <div className="space-y-4">
                        <div className="flex justify-between items-center">
                          <p className="text-sm text-gray-500">Revenue at 100% Occupancy</p>
                          <p className="text-lg font-bold">${analytics.financial.potential_revenue}</p>
                        </div>
                        <div className="flex justify-between items-center">
                          <p className="text-sm text-gray-500">Monthly Revenue Gap</p>
                          <p className="text-lg font-bold">${analytics.financial.revenue_gap}</p>
                        </div>
                        <div className="flex justify-between items-center">
                          <p className="text-sm text-gray-500">Annual Revenue Impact</p>
                          <p className="text-lg font-bold">${analytics.financial.annual_revenue_impact}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            
            {/* Trends Tab */}
            <TabsContent value="trends">
              <Card>
                <CardHeader>
                  <CardTitle>Historical & Seasonal Trends</CardTitle>
                  <CardDescription>Long-term performance trends and seasonal patterns</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Historical Occupancy Trends */}
                    <div>
                      <h3 className="font-medium mb-4">Historical Occupancy Trends</h3>
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart
                            data={historical_trends}
                            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="month" />
                            <YAxis />
                            <Tooltip />
                            <Legend />
                            <Line
                              type="monotone"
                              dataKey="average_occupancy"
                              name="Avg. Occupancy (%)"
                              stroke="#8884d8"
                              activeDot={{ r: 8 }}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                    
                    {/* Seasonal Trends */}
                    <div>
                      <h3 className="font-medium mb-4">Seasonal Patterns</h3>
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart
                            data={seasonal_trends.leads_by_month}
                            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="month" />
                            <YAxis />
                            <Tooltip />
                            <Legend />
                            <Bar dataKey="count" name="Leads" fill="#82ca9d" />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-6">
                    <h3 className="font-medium mb-4">Tour Seasonality</h3>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={seasonal_trends.tours_by_month}
                          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="month" />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Bar dataKey="count" name="Tours" fill="#8884d8" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                  
                  <div className="mt-6 p-4 border rounded-md bg-gray-50">
                    <h3 className="font-medium mb-2">Insights</h3>
                    <ul className="list-disc pl-5 space-y-2">
                      <li>Compare occupancy trends to industry benchmarks to identify opportunities for improvement.</li>
                      <li>Analyze seasonal patterns to optimize marketing efforts during low-demand periods.</li>
                      <li>Consider adjusting pricing strategy based on seasonal demand fluctuations.</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </MainLayout>
  );
}
